import { useMemo } from "react";
import { toNumber } from "@/shared/utils";
import { INCOME_STATUS_ORDER } from "../consts";
import type { IncomeRecordListItem } from "../types";

export type IncomeSortKey =
  | "status"
  | "date"
  | "amount"
  | "source"
  | "pool"
  | "type"
  | "document_number";

export interface IncomeSortConfig {
  key: IncomeSortKey;
  direction: "asc" | "desc";
}

export function useIncomeSort(
  records: IncomeRecordListItem[],
  sortConfig: IncomeSortConfig | null,
): IncomeRecordListItem[] {
  return useMemo(() => {
    if (!sortConfig) {
      return records;
    }

    const { key, direction } = sortConfig;

    return [...records].sort((a, b) => {
      let cmp = 0;

      if (key === "status") {
        const aStatus = INCOME_STATUS_ORDER[a.status] ?? -1;
        const bStatus = INCOME_STATUS_ORDER[b.status] ?? -1;
        cmp = aStatus - bStatus;
      } else if (key === "date") {
        const aDate = new Date(a.document_date).getTime();
        const bDate = new Date(b.document_date).getTime();
        cmp = aDate - bDate;
      } else if (key === "amount") {
        cmp = toNumber(a.amount) - toNumber(b.amount);
      } else if (key === "source") {
        cmp = a.source_name.localeCompare(b.source_name);
      } else if (key === "pool") {
        cmp = a.pool.localeCompare(b.pool);
      } else if (key === "type") {
        cmp = a.income_type.localeCompare(b.income_type);
      } else if (key === "document_number") {
        cmp = a.document_number.localeCompare(b.document_number);
      }

      return direction === "asc" ? cmp : -cmp;
    });
  }, [records, sortConfig]);
}

export function toggleIncomeSort(
  currentSort: IncomeSortConfig | null,
  newKey: IncomeSortKey,
): IncomeSortConfig {
  if (currentSort?.key === newKey) {
    return { key: newKey, direction: currentSort.direction === "asc" ? "desc" : "asc" };
  }
  return { key: newKey, direction: "asc" };
}
